import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha, useTheme } from "@mui/material/styles";
import ExtensionRoundedIcon from "@mui/icons-material/ExtensionRounded";

import { createAddonCheckout, getAddons } from "../../api/kitchen";

const formatPrice = (value) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Paid extras on top of the plan. Each is its own Stripe subscription, so it is
 * bought here and cancelled through the same Stripe portal as the plan.
 */
export default function AddonsSection() {
  const theme = useTheme();
  const [addons, setAddons] = useState(null);
  const [buying, setBuying] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getAddons()
      .then(({ data }) => setAddons(data))
      .catch(() => setAddons([]));
  }, []);

  const buy = async (code) => {
    setBuying(code);
    setError("");
    try {
      const { data } = await createAddonCheckout(code);
      window.location.href = data.url;
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível abrir o pagamento.");
      setBuying(null);
    }
  };

  if (!addons || addons.length === 0) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: theme.palette.text.primary }}>
        Adicionais
      </Typography>
      {error && (
        <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 1 }}>
          {error}
        </Typography>
      )}
      <Stack spacing={1.5}>
        {addons.map((addon) => {
          const usage = addon.usage;
          const pct = usage && !usage.unlimited && usage.limit > 0 ? Math.min(100, (usage.used / usage.limit) * 100) : 0;
          return (
            <Paper
              key={addon.code}
              variant="outlined"
              sx={{ p: 2, borderRadius: "6px", borderColor: theme.palette.divider }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                >
                  <ExtensionRoundedIcon />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{addon.name}</Typography>
                    {addon.active && (
                      <Chip
                        size="small"
                        label={addon.cancel_at_period_end ? "Cancela no fim do período" : "Ativo"}
                        color={addon.cancel_at_period_end ? "warning" : "success"}
                      />
                    )}
                    {addon.status === "past_due" && <Chip size="small" label="Pagamento pendente" color="error" />}
                  </Stack>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {addon.description}
                  </Typography>
                  {usage && (
                    <Box sx={{ mt: 1, maxWidth: 360 }}>
                      {!usage.unlimited && <LinearProgress variant="determinate" value={pct} sx={{ borderRadius: 4, height: 6 }} />}
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {usage.unlimited
                          ? `${usage.used} ${usage.unit_label} usados neste período`
                          : `${usage.used} de ${usage.limit} ${usage.unit_label} usados neste período`}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ textAlign: { sm: "right" }, flexShrink: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                    {formatPrice(addon.price_monthly)}
                    <Typography component="span" variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {" "}/ mês
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", color: theme.palette.text.secondary }}>
                    {addon.included_units_per_period === -1
                      ? `${addon.unit_label} ilimitados`
                      : `${addon.included_units_per_period} ${addon.unit_label}/mês`}
                  </Typography>
                  {!addon.active && addon.purchasable && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={Boolean(buying)}
                      onClick={() => buy(addon.code)}
                      startIcon={buying === addon.code ? <CircularProgress size={14} color="inherit" /> : null}
                      sx={{ mt: 1, textTransform: "none", fontWeight: 700, borderRadius: "14px" }}
                    >
                      Ativar
                    </Button>
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
